import React, { useState, useEffect, useRef } from 'react';
import _ from 'lodash';
import { makeStyles } from 'design-system';
import { InstructionComponentType } from '@scale/shared/defaults';
import { neverfail } from '@scale/shared/projectInstruction/utils';
import Label from '../typeComponents/label';
import RichText from '../typeComponents/richText';
import Attribute from '../typeComponents/attribute';
import Field from '../typeComponents/field';
import Rule from '../typeComponents/rule';
import Attachment from '../typeComponents/attachment';
import Group from '../typeComponents/group';
import { ComponentJson } from '../types';
import { useProjectInstructionContext } from '../projectInstructionContext';
import cn from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import mediumZoom, { MediumZoomStyles } from '../mediumZoom';
import { getEachSubComponentDataFromId } from 'react-scale/components/scalers/instruction/utils';

// eslint-disable-next-line complexity
const Details = ({
  contentData,
  index,
  subAttributeContentData,
  subLabelContentData,
  depth = 0,
}: {
  contentData?: ComponentJson;
  index?: number | number[];
  subAttributeContentData?: ComponentJson[];
  subLabelContentData?: ComponentJson[];
  depth?: number;
}) => {
  const classes = useStyles();
  const {
    isMobileView,
    setSelectedPath,
    isRapidPreview,
    scrollIntoView,
    scrollToggle,
    components,
  } = useProjectInstructionContext();
  const contentDataType = contentData?.type;
  const elementRef = useRef<HTMLDivElement>(null);
  // default only the first section expanded
  const [expanded, setExpanded] = useState(index === 0);

  // Apply zoomable effect to all inline images after component has fully rendered and when component is expanded in preview mode
  useEffect(() => {
    mediumZoom(document.querySelectorAll('p img'));
  }, [contentData, expanded]);

  useEffect(() => {
    if ((scrollIntoView === index || _.isEqual(scrollIntoView, index)) && elementRef?.current) {
      elementRef.current.style.display = 'grid';
      setExpanded(true);
      setTimeout(
        () =>
          elementRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          }),
        50,
      );
    } else if (Array.isArray(scrollIntoView) && index === scrollIntoView[0]) {
      setExpanded(true);
    }
  }, [index, scrollIntoView, scrollToggle]);

  let Comp: any = () => null;

  switch (contentDataType) {
    case InstructionComponentType.RichText:
      Comp = RichText;
      break;
    case InstructionComponentType.Label:
      Comp = Label;
      break;
    case InstructionComponentType.Attribute:
      Comp = Attribute;
      break;
    case InstructionComponentType.Field:
      Comp = Field;
      break;
    case InstructionComponentType.Rule:
      Comp = Rule;
      break;
    case InstructionComponentType.Attachment:
      Comp = Attachment;
      break;
    case InstructionComponentType.Group:
      Comp = Group;
      break;
    default:
      neverfail(contentDataType as never);
  }

  // in rapid preview mode, we want to make the sections all appear and be expandable and collapsable.
  return (
    <div
      className={cn(
        classes.commonDetailStyles,
        isMobileView
          ? classes.sidePanelContainer
          : isRapidPreview
          ? expanded
            ? classes.containerRapidPreview
            : classes.containerSmall
          : classes.container,
      )}
      ref={elementRef}
      id={contentData?._id ?? ''}
    >
      <MediumZoomStyles />
      {isRapidPreview && !expanded && !!contentData && (
        <div className={classes.titleContainer} onClick={() => setExpanded(true)}>
          <div className={classes.titleWithChevron}>
            <FontAwesomeIcon size="sm" icon="chevron-right" />
            <h3
              className={classes.titleH3}
              dangerouslySetInnerHTML={{ __html: contentData.title || '' }}
            ></h3>
          </div>
          {contentData.hiddenFromCustomer && (
            <FontAwesomeIcon className={classes.hideIcon} icon="eye-slash" />
          )}
        </div>
      )}
      {isRapidPreview && expanded && (
        <div className={classes.collapseOverlay} onClick={() => setExpanded(false)} />
      )}
      {(!isRapidPreview || expanded) && (
        <Comp contentData={contentData} setSelectedPath={setSelectedPath} index={index}></Comp>
      )}
      {subAttributeContentData && subAttributeContentData.length > 0 && (
        <div className={classes.subComponentSectionHead}>
          <h3>SubAttributes:</h3>
        </div>
      )}
      {subAttributeContentData?.map((subAttrComponent: ComponentJson) => {
        return (
          <Details
            key={subAttrComponent._id}
            contentData={subAttrComponent}
            index={index}
            depth={depth ? depth + 1 : 1}
            subAttributeContentData={
              subAttrComponent?.nestedAttributeIds
                ? getEachSubComponentDataFromId(components, subAttrComponent.nestedAttributeIds)
                : []
            }
            subLabelContentData={
              subAttrComponent?.nestedLabelIds
                ? getEachSubComponentDataFromId(components, subAttrComponent.nestedLabelIds)
                : []
            }
          />
        );
      })}
      {subLabelContentData && subLabelContentData.length > 0 && (
        <div className={classes.subComponentSectionHead}>
          <h3>SubLabels:</h3>
        </div>
      )}
      {subLabelContentData?.map((subLabelComponent: ComponentJson) => {
        return (
          <Details
            key={subLabelComponent._id}
            contentData={subLabelComponent}
            index={index}
            depth={depth ? depth + 1 : 1}
            subAttributeContentData={
              subLabelComponent?.nestedAttributeIds
                ? getEachSubComponentDataFromId(components, subLabelComponent.nestedAttributeIds)
                : []
            }
            subLabelContentData={
              subLabelComponent?.nestedLabelIds
                ? getEachSubComponentDataFromId(components, subLabelComponent.nestedLabelIds)
                : []
            }
          />
        );
      })}
    </div>
  );
};

const useStyles = makeStyles(theme => ({
  container: {
    border: `1px solid ${theme.colors.gray10}`,
    background: 'white',
    padding: '10px 20px',
    borderRadius: 10,
    color: theme.colors.gray75,
    flex: 3,
    marginLeft: 20,
    '@global ins': {
      textDecoration: 'underline',
      textDecorationColor: '#32a82a',
      textDecorationThickness: '3px',
      backgroundColor: 'rgba(66, 171, 61, 0.28)',
    },
    '@global del': {
      textDecoration: 'line-through',
      textDecorationColor: 'red',
      textDecorationThickness: '2px',
      backgroundColor: 'rgba(225, 66, 66, 0.28)',
    },
  },
  chevronExpanded: {},
  commonDetailStyles: {
    position: 'relative',
    '& table, th, td': {
      paddingTop: 4,
      paddingLeft: 4,
      border: '1px solid',
      borderCollapse: 'collapse',
    },
    '& h3': {
      fontSize: '1.125rem',
      lineHeight: 2,
      marginTop: 0,
      marginBottom: 0,
    },
    '& h3 p': {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  sidePanelContainer: {
    height: '100%',
    overflow: 'scroll',
    paddingBottom: 100,
    '@global ins': {
      textDecoration: 'underline',
      textDecorationColor: '#32a82a',
      textDecorationThickness: '3px',
      backgroundColor: 'rgba(66, 171, 61, 0.28)',
    },
    '@global del': {
      textDecoration: 'line-through',
      textDecorationColor: 'red',
      textDecorationThickness: '2px',
      backgroundColor: 'rgba(225, 66, 66, 0.28)',
    },
  },
  containerRapidPreview: {
    border: `1px solid ${theme.colors.gray10}`,
    background: 'white',
    padding: '10px 20px',
    borderRadius: 10,
    color: theme.colors.gray75,
    marginLeft: 20,
  },
  containerSmall: {
    border: `1px solid ${theme.colors.gray10}`,
    background: 'white',
    padding: '10px 20px',
    borderRadius: 10,
    color: theme.colors.gray75,
    marginLeft: 20,
  },
  titleContainer: {
    flexDirection: 'row',
    display: 'flex',
    gap: 7,
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
  },
  titleWithChevron: {
    display: 'flex',
    gap: 7,
    alignItems: 'center',
  },
  titleH3: {
    'white-space': 'nowrap',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
  },
  subComponentSectionHead: {
    marginTop: 20,
    marginBottom: 10,
    marginLeft: 20,
    'white-space': 'nowrap',
    overflow: 'hidden',
    'text-overflow': 'ellipsis',
  },
  collapseOverlay: {
    position: 'absolute',
    height: '40px',
    width: '100%',
    cursor: 'pointer',
  },
  hideIcon: {
    color: theme.colors.gray50,
  },
}));

export default Details;
